/* eslint-disable @typescript-eslint/no-misused-promises */
import { useMemo, useState } from "react"
import { Platform, View } from "react-native"
import { useAnalyses } from "@/queries/analysis"
import TextTabs, { TextTab } from "@/components/tabs/TextTabs"
import { TouchableOpacity } from "react-native-gesture-handler"
import SortArrows from "@/assets/icons/sort-arrows.svg"
import { propertiesViewed, sortClicked } from "@/analytics/events/properties"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { H2 } from "@/components/standard/BFText"
import {
  ANALYSIS_TAB_LABELS,
  type AnalysisListType,
} from "@/screens/analyze/utils/Properties"
import { AnalysisList } from "@/screens/analyze/components/AnalysisList"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import ReusableSortPopup, {
  type SortSectionConfig,
} from "@/components/sorting/ReusableSortPopup"
import type { AnalysesSortOption, SortDirectionOption } from "@/types"
import { combineSortAndDirection, getOrderingLabels } from "@/utils/AnalysisUtils"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

export default function PropertiesScreen() {
  const [sortOpen, setSortOpen] = useState(false)
  const [tab, setTab] = useState<AnalysisListType>("all")
  const [sort, setSort] = useState<AnalysesSortOption>("updated_at")
  const [direction, setDirection] = useState<SortDirectionOption>("default")
  const { isWeb } = useDeviceAttributes()

  const analysesQuery = useAnalyses(
    combineSortAndDirection(sort, direction),
    tab === "favorites",
    false
  )

  const hasAnalyses = Boolean(
    analysesQuery.data?.pages?.[0]?.analyses?.length ?? 0
  )

  const sortPopupTop: number = Platform.select({
    android: 64,
    ios: 30,
    web: 60,
    default: 120,
  })

  const analysisSortingSections: SortSectionConfig[] = useMemo(() => {
    const orderingLabels = getOrderingLabels(sort)
    const sections: Array<SortSectionConfig | null> = [
      {
        header: "ACTIVITY",
        options: [
          { label: "Date", value: "updated_at", defaultDirection: "reverse" },
          { label: "Profit", value: "profit" },
          { label: "ARV", value: "after_repair_value" },
          { label: "Purchase Price", value: "purchase_price" },
          { label: "Beds", value: "bedrooms" },
          { label: "Year Built", value: "year_built" },
        ],
      },
      orderingLabels
        ? {
            header: "ORDERING",
            options: [
              { label: orderingLabels.ascending, value: "default" },
              { label: orderingLabels.descending, value: "reverse" },
            ],
          }
        : null,
    ]
    return sections.filter(Boolean) as SortSectionConfig[]
  }, [sort])

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    propertiesViewed({
      currentTab: tab,
    })
  })

  const headerContent = (
    <View className="flex flex-col justify-end py-2">
      <View className="flex-row justify-between items-end">
        <H2 className="text-lightest">{"Properties"}</H2>
        <View className="flex flex-row">
          <TouchableOpacity
            testID="sort-button"
            disabled={!hasAnalyses}
            onPress={() => {
              if (!sortOpen) {
                sortClicked()
                setSortOpen(true)
              } else {
                setSortOpen(false)
              }
            }}
          >
            {hasAnalyses ? <SortArrows /> : null}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  )

  const entryObjects = Object.entries(ANALYSIS_TAB_LABELS)

  const onSelectSort = (value: string) => {
    const selectedOption = analysisSortingSections
      .flatMap((section) => section.options)
      .find((option) => option.value === value)

    setSort(value as AnalysesSortOption)
    setDirection(selectedOption?.defaultDirection ?? "default")
    setSortOpen(false)
  }

  return (
    <BFScreen
      className="flex-col"
      noBackButton={true}
      headerSticky={false}
      dark={true}
      gradientBackground={true}
      includeStandardSideMargins={true}
      header={headerContent}
    >
      <View className="flex-1">
        <TextTabs hasFullWidthLine={!isWeb}>
          {entryObjects.map(([key, label]) => {
            return (
              <TextTab
                label={label}
                value={key}
                key={key}
                selected={key === tab}
                onSelect={() => {
                  setTab(key)
                }}
                size={entryObjects.length > 3 ? "sm" : "lg"}
                dark={true}
              />
            )
          })}
        </TextTabs>

        <AnalysisList
          analysesQuery={analysesQuery}
          onlyFavorites={tab === "favorites"}
        />
        <ReusableSortPopup
          displays={sortOpen}
          sort={sort}
          direction={direction}
          sections={analysisSortingSections}
          positionClassName={`right-0 top-[${sortPopupTop}px]`}
          onSelectSort={onSelectSort}
          onSelectDirection={(direction: SortDirectionOption) => {
            setDirection(direction)
            setSortOpen(false)
          }}
          onClose={() => {
            setSortOpen(false)
          }}
        />
      </View>
    </BFScreen>
  )
}
